/**
 * Core Virtual Scrolling Hook
 * Provides efficient rendering of large lists with theme-aware sizing
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
/**
 * Default range extractor that includes overscan
 */
const defaultRangeExtractor = (range, overscan) => {
    const start = Math.max(0, range.start - overscan);
    const end = range.end + overscan;
    const items = [];
    for (let i = start; i <= end; i++) {
        items.push(i);
    }
    return items;
};
/**
 * Core virtual scrolling hook with theme awareness
 */
export function useVirtualScroll({ count, getScrollElement, estimateSize, overscan = 3, horizontal = false, paddingStart = 0, paddingEnd = 0, scrollMargin = 0, getItemKey = (index) => index, rangeExtractor = (range) => defaultRangeExtractor(range, overscan), onChange }) {
    const [scrollOffset, setScrollOffset] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);
    const measurementsCache = useRef(new Map());
    const scrollElement = useRef(null);
    const scrollTimeoutId = useRef(undefined);
    // Get scroll element
    useEffect(() => {
        scrollElement.current = getScrollElement();
    }, [getScrollElement]);
    // Calculate item positions and sizes
    const { virtualItems, totalSize, start, end } = useMemo(() => {
        if (count === 0) {
            return { virtualItems: [], totalSize: 0, start: 0, end: 0 };
        }
        let totalSize = paddingStart;
        const itemSizes = new Map();
        // Calculate sizes for all items
        for (let i = 0; i < count; i++) {
            const measuredSize = measurementsCache.current.get(i);
            const size = measuredSize ?? estimateSize(i);
            itemSizes.set(i, size);
            totalSize += size;
        }
        totalSize += paddingEnd;
        // Calculate which items are visible
        const scrollElementSize = horizontal
            ? scrollElement.current?.clientWidth ?? 0
            : scrollElement.current?.clientHeight ?? 0;
        const startOffset = scrollOffset - scrollMargin;
        const endOffset = scrollOffset + scrollElementSize + scrollMargin;
        let start = 0;
        let end = count - 1;
        // Find start index
        let currentOffset = paddingStart;
        for (let i = 0; i < count; i++) {
            const size = itemSizes.get(i) ?? estimateSize(i);
            if (currentOffset + size >= startOffset) {
                start = i;
                break;
            }
            currentOffset += size;
        }
        // Find end index
        currentOffset = paddingStart;
        for (let i = 0; i < count; i++) {
            const size = itemSizes.get(i) ?? estimateSize(i);
            if (currentOffset >= endOffset) {
                end = i - 1;
                break;
            }
            currentOffset += size;
        }
        // Get range to render (with overscan)
        const rangeToRender = rangeExtractor({
            start: Math.max(0, start),
            end: Math.min(count - 1, end)
        });
        // Create virtual items
        const virtualItems = [];
        let itemStart = paddingStart;
        rangeToRender.forEach(index => {
            // Calculate start position
            let calculatedStart = paddingStart;
            for (let i = 0; i < index; i++) {
                calculatedStart += itemSizes.get(i) ?? estimateSize(i);
            }
            const size = itemSizes.get(index) ?? estimateSize(index);
            virtualItems.push({
                index,
                start: calculatedStart,
                size,
                end: calculatedStart + size
            });
        });
        return { virtualItems, totalSize, start, end };
    }, [
        count,
        scrollOffset,
        estimateSize,
        paddingStart,
        paddingEnd,
        scrollMargin,
        horizontal,
        rangeExtractor
    ]);
    // Notify of range change outside of render phase
    useEffect(() => {
        if (onChange && virtualItems.length > 0) {
            onChange({ start, end });
        }
    }, [start, end, onChange, virtualItems.length]);
    // Handle scroll events
    const handleScroll = useCallback(() => {
        const element = scrollElement.current;
        if (!element)
            return;
        const offset = horizontal ? element.scrollLeft : element.scrollTop;
        setScrollOffset(offset);
        setIsScrolling(true);
        // Clear existing timeout
        if (scrollTimeoutId.current) {
            clearTimeout(scrollTimeoutId.current);
        }
        // Set scrolling to false after scroll ends
        scrollTimeoutId.current = setTimeout(() => {
            setIsScrolling(false);
        }, 150);
    }, [horizontal]);
    // Set up scroll listener
    useEffect(() => {
        const element = scrollElement.current;
        if (!element)
            return;
        element.addEventListener('scroll', handleScroll, { passive: true });
        // Initial scroll position
        handleScroll();
        return () => {
            element.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutId.current) {
                clearTimeout(scrollTimeoutId.current);
            }
        };
    }, [handleScroll]);
    // Measure element size and update cache
    const measureElement = useCallback((element) => {
        if (!element)
            return;
        const index = parseInt(element.getAttribute('data-index') ?? '0', 10);
        const size = horizontal ? element.offsetWidth : element.offsetHeight;
        const currentSize = measurementsCache.current.get(index);
        if (currentSize !== size) {
            measurementsCache.current.set(index, size);
            // Trigger re-calculation on next render
            setScrollOffset(prev => prev);
        }
    }, [horizontal]);
    // Scroll to specific index
    const scrollToIndex = useCallback((index, options = {}) => {
        const element = scrollElement.current;
        if (!element || index < 0 || index >= count)
            return;
        const { align = 'start' } = options;
        // Calculate position of target item
        let targetOffset = paddingStart;
        for (let i = 0; i < index; i++) {
            const size = measurementsCache.current.get(i) ?? estimateSize(i);
            targetOffset += size;
        }
        const itemSize = measurementsCache.current.get(index) ?? estimateSize(index);
        const scrollElementSize = horizontal
            ? element.clientWidth
            : element.clientHeight;
        let scrollTo = targetOffset;
        if (align === 'center') {
            scrollTo = targetOffset - (scrollElementSize - itemSize) / 2;
        }
        else if (align === 'end') {
            scrollTo = targetOffset - scrollElementSize + itemSize;
        }
        // Ensure scroll position is within bounds
        const maxScroll = totalSize - scrollElementSize;
        scrollTo = Math.max(0, Math.min(maxScroll, scrollTo));
        if (horizontal) {
            element.scrollLeft = scrollTo;
        }
        else {
            element.scrollTop = scrollTo;
        }
    }, [count, paddingStart, estimateSize, totalSize, horizontal]);
    // Scroll to specific offset
    const scrollToOffset = useCallback((offset) => {
        const element = scrollElement.current;
        if (!element)
            return;
        if (horizontal) {
            element.scrollLeft = offset;
        }
        else {
            element.scrollTop = offset;
        }
    }, [horizontal]);
    return {
        virtualItems,
        totalSize,
        scrollOffset,
        scrollToIndex,
        scrollToOffset,
        measureElement
    };
}
